import { v4 as uuidv4 } from "uuid";

import { getClientInfo } from "../repository/ClientRepository";
import { 
  filterOutUndefined, 
  isNullOrBlank, 
  nullIfBlank 
} from "../../utils/Utils";
import { RecordInfo } from "./RecordInfo";
import { 
  ContactPersonInfo, 
  isOnlyUsedForElectricityBilling, 
  isPortalOnlyProject, 
  ProjectDetails, 
  ProjectOperationSource,
  Room,
  stripElectricityBillingPrefix
} from "./ProjectDetails";
import Vue from "vue";
import { 
  AppointmentInfo, 
  DamagePatternConclusionState, 
  DryingProjectOverview, 
  getDamagePatternConclusionStateString 
} from "./DryingProjectOverview";
import { 
  AppointmentType, 
  DamageAssessmentResultStep, 
  getStepDescriptionForLogEntries, 
  labelAndStatusAppointments, 
  ProjectStatusSyncStatus
} from "./ProjectState";
import { joinToString } from "@/utils/LocalisationHelper";
import { formatApartmentDetails, joinApartmentLabel } from "@/utils/ApartmentLabel";
import { getRoomDescription, joinRoomLabel } from "@/utils/RoomLabel";
import { UserLookup } from "./UserLookup";
import { UserModule } from "@/store/modules/UserModule";
import { getDisplayName, getFullName } from "./User";
import { formatJsDate } from "@/utils/DateFormatter";
import { dateToTimestampParser, parseFirestoreTimestampString } from "@/utils/DateHelper";
import { ProjectStepsContainer } from "./ProjectSteps";
import { getRecordInfo } from "../repository/UserRepository";
import { NoteChange } from "./ProjectNote";

export enum ProjectLogSortOrder {
  NEWEST_FIRST = "NEWEST_FIRST",
  OLDEST_FIRST = "OLDEST_FIRST"
}

export enum ProjectLogFilter {
  ALL_LOGS = "ALL_LOGS",
  AUTO_TIME_TRACKING = "AUTO_TIME_TRACKING",
  NOTE_AND_STATUS = "NOTE_AND_STATUS",
  NOTES = "NOTES",
  TIME_TRACKING = "TIME_TRACKING",
}

export enum ProjectEventType {
  AUTO_TIME_TRACKING = "AUTO_TIME_TRACKING",
  CLIENT_DATA_COMPLETED = "CLIENT_DATA_COMPLETED",
  CONTACT_PERSON_ADDED = "CONTACT_PERSON_ADDED",
  CONTACT_PERSON_DELETED = "CONTACT_PERSON_DELETED",
  DAMAGE_ASSESSMENT_COMPLETED = "DAMAGE_ASSESSMENT_COMPLETED",
  DAMAGE_ASSESSMENT_PLANNED = "DAMAGE_ASSESSMENT_PLANNED",
  DAMAGE_ASSESSMENT_STARTED = "DAMAGE_ASSESSMENT_STARTED",
  DAMAGE_PATTERN_COMPLETED = "DAMAGE_PATTERN_COMPLETED",
  DAMAGE_PATTERN_REOPENED = "DAMAGE_PATTERN_REOPENED",
  DAMAGE_REPORT_SENT = "DAMAGE_REPORT_SENT",
  DEVICES_SETUP_COMPLETED = "DEVICES_SETUP_COMPLETED",
  DOCUMENTATION_AND_LEAK_DETECTION_FINISHED = "DOCUMENTATION_AND_LEAK_DETECTION_FINISHED",
  DRYING_FINISHED = "DRYING_FINISHED",
  DRYING_NECESSARY = "DRYING_NECESSARY",
  DRYING_PROGRESS_COMPLETED_FOLLOW_UP = "DRYING_PROGRESS_COMPLETED_FOLLOW_UP",
  DRYING_PROGRESS_COMPLETED_MEASUREMENT = "DRYING_PROGRESS_COMPLETED_MEASUREMENT",
  DRYING_RESULT = "DRYING_RESULT",
  DRYING_SET_UP_STARTED = "DRYING_SET_UP_STARTED",
  DRYING_SETUP_COMPLETED = "DRYING_SETUP_COMPLETED",
  INTERIM_REPORT_SENT = "INTERIM_REPORT_SENT",
  LEAK_DETECTION_EXECUTION_COMPLETED = "LEAK_DETECTION_EXECUTION_COMPLETED",
  LEAK_DETECTION_PREPARATION_COMPLETED = "LEAK_DETECTION_PREPARATION_COMPLETED",
  LEAK_DETECTION_PROJECT_FINISHED = "LEAK_DETECTION_PROJECT_FINISHED",
  LEAK_DETECTION_PROJECT_REOPENED = "LEAK_DETECTION_PROJECT_REOPENED",
  LEAK_DETECTION_REPORT_SENT = "LEAK_DETECTION_REPORT_SENT",
  LEAK_DETECTION_STARTED = "LEAK_DETECTION_STARTED",
  MEASUREMENT_CREATED = "MEASUREMENT_CREATED",
  MEASUREMENT_SERIES_CREATED = "MEASUREMENT_SERIES_CREATED",
  MEASUREMENT_SERIES_EDITED = "MEASUREMENT_SERIES_EDITED",
  NOTE = "NOTE",
  PROJECT_ASSIGNED = "PROJECT_ASSIGNED",
  PROJECT_COMPLETED = "PROJECT_COMPLETED",
  PROJECT_COMPLETED_FINAL_TILE = "PROJECT_COMPLETED_FINAL_TILE",
  PROJECT_CONCLUSION_COMPLETED = "PROJECT_CONCLUSION_COMPLETED",
  PROJECT_CREATED = "PROJECT_CREATED",
  PROJECT_DELETED = "PROJECT_DELETED",
  PROJECT_DELETED_IN_APP = "PROJECT_DELETED_IN_APP",
  PROJECT_MOVED_TO_ARCHIVED = "PROJECT_MOVED_TO_ARCHIVED",
  PROJECT_MOVED_TO_FINISHED = "PROJECT_MOVED_TO_FINISHED",
  PROJECT_REACTIVATED = "PROJECT_REACTIVATED",
  PROJECT_REOPENED = "PROJECT_REOPENED",
  PROJECT_REPORT_SENT = "PROJECT_REPORT_SENT",
  PROJECT_RESTORED = "PROJECT_RESTORED",
  PROJECT_RESULT = "PROJECT_RESULT",
  PROJECT_SECTION_FINISHED = "PROJECT_SECTION_FINISHED",
  PROJECT_SECTION_STARTED = "PROJECT_SECTION_STARTED",
  PROJECT_WORK_STARTED = "PROJECT_WORK_STARTED",
  ROOM_ADDED = "ROOM_ADDED",
  ROOM_DELETED = "ROOM_DELETED",
  STARTED_CACHED_PROJECT_EDIT = "STARTED_CACHED_PROJECT_EDIT",
  STEP_DAMAGE_ASSESSMENT_APPOINTMENT = "STEP_DAMAGE_ASSESSMENT_APPOINTMENT",
  STEP_DAMAGE_ASSESSMENT_RESULT = "STEP_DAMAGE_ASSESSMENT_RESULT",
  STEP_DRYING_CONCLUSION_APPOINTMENTS = "STEP_DRYING_CONCLUSION_APPOINTMENTS",
  STEP_DRYING_CONCLUSION_RESULTS = "STEP_DRYING_CONCLUSION_RESULTS",
  STEP_DRYING_SET_UP_APPOINTMENTS = "STEP_DRYING_SET_UP_APPOINTMENTS",
  STEP_DRYING_SET_UP_COMPLETIONS = "STEP_DRYING_SET_UP_COMPLETIONS",
  STEP_FIRST_CONTACT = "STEP_FIRST_CONTACT",
  STEP_INTERMEDIATE_MEASUREMENT_APPOINTMENTS = "STEP_INTERMEDIATE_MEASUREMENT_APPOINTMENTS",
  STEP_INTERMEDIATE_MEASUREMENT_COMPLETIONS = "STEP_INTERMEDIATE_MEASUREMENT_COMPLETIONS",
  STEP_LEAK_DETECTION_APPOINTMENT = "STEP_LEAK_DETECTION_APPOINTMENT",
  STEP_LEAK_DETECTION_RESULT = "STEP_LEAK_DETECTION_RESULT",
  STEP_OFFER_RESULT_DRYING_APPROVED = "STEP_OFFER_RESULT_DRYING_APPROVED",
  STEP_OFFER_RESULT_DRYING_NOT_APPROVED = "STEP_OFFER_RESULT_DRYING_NOT_APPROVED",
  STEP_OFFER_SEND_TO_INSURANCE = "STEP_OFFER_SEND_TO_INSURANCE",
  STEP_PROJECT_ACCEPTED = "STEP_PROJECT_ACCEPTED",
  STEP_PROJECT_APPROVED_PROJECT_FINISHED = "STEP_PROJECT_APPROVED_PROJECT_FINISHED",
  STEP_PROJECT_APPROVED_PROJECT_SAVED = "STEP_PROJECT_APPROVED_PROJECT_SAVED",
  STEP_PROJECT_RESULT = "STEP_PROJECT_RESULT",
  STEP_REPAIR_APPOINTMENTS = "STEP_REPAIR_APPOINTMENTS",
  STEP_REPAIR_START = "STEP_REPAIR_START",
  TIME_TRACKING = "TIME_TRACKING",
  TIME_TRACKING_REPORT_SENT = "TIME_TRACKING_REPORT_SENT",
  REPAIR_STARTED = "REPAIR_STARTED",
  REPAIR_COMPLETED = "REPAIR_COMPLETED",
  UNKNOWN = "UNKNOWN"
}

const PROJECT_STATUS_LOGS = [
  ProjectEventType.PROJECT_DELETED,
  ProjectEventType.PROJECT_MOVED_TO_ARCHIVED,
  ProjectEventType.PROJECT_MOVED_TO_FINISHED,
  ProjectEventType.PROJECT_REACTIVATED,
  ProjectEventType.PROJECT_REOPENED,
  ProjectEventType.PROJECT_RESTORED,
];

export enum ProjectSection {
  CLIENT_DATA = "CLIENT_DATA",
  DOCUMENTATION_AND_LEAK_DETECTION = "DOCUMENTATION_AND_LEAK_DETECTION",
  DAMAGE_PATTERN = "DAMAGE_PATTERN",
  DAMAGE_PATTERN_CONCLUSION = "DAMAGE_PATTERN_CONCLUSION",
  SETUP_DRYING = "SETUP_DRYING",
  DEVICES = "DEVICES",
  SETUP_DEVICES = "SETUP_DEVICES",
  DRYING_PROGRESS = "DRYING_PROGRESS",
  PACKUP_DEVICES = "PACKUP_DEVICES",
  DRYING_PROJECT_CONCLUSION = "DRYING_PROJECT_CONCLUSION",
  PREPARE_LEAK_DETECTION = "PREPARE_LEAK_DETECTION",
  EXECUTE_LEAK_DETECTION = "EXECUTE_LEAK_DETECTION",
  LEAK_DETECTION_PROJECT_CONCLUSION = "LEAK_DETECTION_PROJECT_CONCLUSION",
}

export interface SectionInfo {
  projectSection: ProjectSection;
}

export interface DryingResultInfo {
  wasDryingSuccessful: boolean;
}

export interface RoomInfo {
  isFromDamageAssesment?: boolean;
  roomName: string;
  apartmentName: string;
}

export interface DamageAssessmentPlannedInfo {
  userId?: string;
  date?: string;
}

export interface DryingNecessaryInfo {
  technicianId?: string;
  conclusionState?: DamagePatternConclusionState;
  isDryingNecessary?: boolean;
}

export interface ProjectCompletedValue {
  wasDryingSuccessful?: boolean;
}

export interface MeasurementCreationInfo {
  measurementSeriesNumber: number;
}

export interface MeasurementSeriesCreationInfo {
  measurementSeriesNumber: number;
  description?: string;
  isUserEdited: boolean;
}

export interface NewProjectCreatedInfo {
  damageDay?: string;
}

export interface ContactPersonEventInfo {
  contactPersonNumber: number;
}

export interface ProjectAssignedInfo {
  newProjectOwnerId: string;
}

export enum ProjectLogType {
  EVENT = "EVENT",
  NOTE = "NOTE",
  STATUS = "STATUS",
}

export interface ProjectEventLog {
  id: string;
  event: ProjectEventType;
  createdBy: RecordInfo;
  info?: string;
  source?: ProjectOperationSource;
  logType: ProjectLogType;
}

export interface NoteRoomInfo {
  roomId: string;
  contactPersonId: string;
  roomNumber: number;
  roomName: string;
}

export interface NoteApartmentInfo {
  apartmentName: string;
  contactPersonId: string;
}

export interface NoteRecipient {
  recipient: string;
  read: boolean;
}

export interface ProjectNoteLog {
  id: string;
  createdBy: RecordInfo;
  event: ProjectEventType.NOTE;
  useForReport: boolean;
  note?: string;
  targetRoom?: NoteRoomInfo | null;
  targetApartment?: NoteApartmentInfo | null;
  recipients: NoteRecipient[];
  source?: ProjectOperationSource;
  logType: ProjectLogType;
}

export interface ProjectStatusLog {
  id: string;
  createdBy: RecordInfo;
  content: string;
  logType: ProjectLogType;
  source: ProjectOperationSource;
}

export interface ProjectLogData {
  eventLogs?: Record<string, ProjectEventLog>;
  noteLogs?: Record<string, ProjectNoteLog>;
}

export interface PendingProjectNoteSync {
  noteChange: NoteChange;
  syncedWith: Record<string, ProjectStatusSyncStatus>;
}

export interface ProjectSyncedNotes {
  externalNoteIds?: Record<string, string>;
  pendingSyncs?: Record<string, PendingProjectNoteSync>;
  notes: Record<string, ProjectNoteLog>;
}

export function getProjectEventLogSortOrderName(
  vue: Vue,
  projectEventLogSortOrder: ProjectLogSortOrder
) {
  switch (projectEventLogSortOrder) {
    case ProjectLogSortOrder.NEWEST_FIRST:
      return vue.$t("projectLogs.eventLogSortOrder.newestFirst");
    case ProjectLogSortOrder.OLDEST_FIRST:
      return vue.$t("projectLogs.eventLogSortOrder.oldestFirst");
    
    default:
      return "";
  }
}

export function getProjectEventLogsFilterName(
  vue: Vue,
  projectEventLogFilter: ProjectLogFilter
) {
  switch (projectEventLogFilter) {
    case ProjectLogFilter.ALL_LOGS:
      return vue.$t("projectLogs.eventLogFilter.allLogs");
    case ProjectLogFilter.AUTO_TIME_TRACKING:
      return vue.$t("projectLogs.eventLogFilter.autoTimeTracking");
    case ProjectLogFilter.NOTE_AND_STATUS:
      return vue.$t("projectLogs.eventLogFilter.noteAndStatus");
    case ProjectLogFilter.NOTES:
      return vue.$t("projectLogs.eventLogFilter.notes");
    case ProjectLogFilter.TIME_TRACKING:
      return vue.$t("projectLogs.eventLogFilter.timeTracking");
      
    default:
      return "";
  }
}

export function isProjectStatusLog(projectLog: ProjectEventLog) {
  if (
    projectLog.event === ProjectEventType.PROJECT_REOPENED &&
    (!projectLog.source || projectLog.source === ProjectOperationSource.PROJECT_MANAGER_APP)
  ) {
    return false;
  }

  return PROJECT_STATUS_LOGS.includes(projectLog.event);
}

export function doesProjectEventLogMatchFilter(
  filter: ProjectLogFilter,
  log: ProjectEventLog | ProjectNoteLog | ProjectStatusLog
) {
  switch (filter) {
    case ProjectLogFilter.ALL_LOGS:
      return true;
    case ProjectLogFilter.NOTE_AND_STATUS:
      return log.logType === ProjectLogType.NOTE || 
        log.logType === ProjectLogType.STATUS ||
        isProjectStatusLog(log as ProjectEventLog);
    case ProjectLogFilter.NOTES:
      return log.logType === ProjectLogType.NOTE;
    
    default:
      return false;
  }
}

export function shouldCreateProjectEventLog() {
  const targetAppApk = Number(getClientInfo()?.targetAppVersion) ?? 0;

  return targetAppApk >= 1676;
}

export function createProjectEventLog(
  projectEventType: ProjectEventType,
  recordInfo: RecordInfo,
  info?: string
): ProjectEventLog {
  return filterOutUndefined({
    id: uuidv4(),
    event: projectEventType,
    createdBy: recordInfo,
    info: info,
    source: ProjectOperationSource.PORTAL
  });
}

export function createProjectNoteLog(
  targetApartment: NoteApartmentInfo | null,
  targetRoom: NoteRoomInfo | null,
  useForReport: boolean,
  note?: string,
  noteId?: string,
): ProjectNoteLog {
  return filterOutUndefined({
    id: noteId ?? uuidv4(),
    event: ProjectLogType.NOTE,
    createdBy: getRecordInfo(),
    note: note,
    recipients: [],
    targetApartment: targetApartment,
    targetRoom: targetRoom,
    useForReport: useForReport,
    source: ProjectOperationSource.PORTAL,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getInfo<T>(info: any): T | null {
  if (!info) {
    return null;
  } else {
    try {
      return JSON.parse(info) as T;
    } catch (e) {
      // [SKIP] Error
      return null;
    }
  }
}

export function formatAppointmentInfo(
  vue: Vue,
  appointmentInfo: AppointmentInfo,
  appointmentType: AppointmentType,
  hasMultipleApartments = false,
) {
  const appointmentDate = isNullOrBlank(appointmentInfo.appointmentDate) 
    ? null
    : new Date(appointmentInfo.appointmentDate);
  
  const rawText = labelAndStatusAppointments(
    vue,
    appointmentDate,
    appointmentType,
    appointmentInfo.apartmentNumber ?? appointmentInfo.contactPersonNumber ?? 0,
    appointmentInfo.responsibleUser ?? null,
    hasMultipleApartments
  );

  const note = appointmentInfo.note;
  if (note) {
    return `${rawText}\n${note}`
  }
  else {
    return rawText;
  }
}

function getDocumentationAndLeakDetectionFinishedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const containsLeakDetectionContent = getInfo<boolean>(projectEventInfo);

  if (containsLeakDetectionContent !== null) {
    if (containsLeakDetectionContent) {
      return vue.$t("projectLogs.eventLogs.documentationAndLeakDetectionFinished").toString();
    }
  }

  return vue.$t("projectLogs.eventLogs.documentationAndLeakDetectionSectionFinished").toString();
}

function getDamageAssessmentPlannedText(
  vue: Vue,
  projectEventInfo?: string,
) {
  const info = getInfo<DamageAssessmentPlannedInfo>(projectEventInfo);

  if (info && info !== null) {
    const userLookup = new UserLookup(UserModule.activeUsersOfClientIncludingIresAdmin);
    const user = info.userId ? userLookup.getUser(info.userId) : null;
    const userDisplayName = user ? getDisplayName(user) : "-";

    let result = vue.$t("projectLogs.eventLogs.damageAssessmentPlanned", [userDisplayName]).toString();

    const date = info.date ? parseFirestoreTimestampString(info.date) : null;
    if (date) {
      result += " " + formatJsDate(date, true);
    }

    return result;
  }

  return "";
}

function getDamageAssesmentCompletedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const isCompeteAssessment = getInfo<boolean>(projectEventInfo);

  if (isCompeteAssessment !== null) {
    if (isCompeteAssessment) {
      return vue.$t("projectLogs.eventLogs.damageAssessmentCompleteFinished").toString();
    }
  }

  return vue.$t("projectLogs.eventLogs.damageAssessmentEasyFinished").toString();
}

function getDamageAssesmentResultText(
  vue: Vue,
  projectEventInfo?: string,
  hasDrying = true
) {
  const info = getInfo<DamageAssessmentResultStep>(projectEventInfo);

  if (info && info !== null) {
    if (info.damagePatternConclusionState) {
      return joinToString(
        [
          vue.$t("projectLogs.eventLogs.stepDamageAssessmentComplete").toString(),
          getDamagePatternConclusionStateString(
            vue, 
            info.damagePatternConclusionState,
            hasDrying
          )
        ],
        ", "
      );
    }
  }

  return vue.$t("projectLogs.eventLogs.stepDamageAssessmentComplete").toString();
}

function getDamagePatternCompletedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const isCompeteAssessment = getInfo<boolean>(projectEventInfo);

  if (isCompeteAssessment !== null) {
    if (isCompeteAssessment) {
      return vue.$t("projectLogs.eventLogs.damageAssessmentCompleteFinished").toString();
    }
  }

  return vue.$t("projectLogs.eventLogs.damageAssessmentEasyFinished").toString();
}


function getDryingResultText(
  vue: Vue,
  projectEventInfo?: string,
  hasDrying = true
) {
  const info = getInfo<DryingResultInfo>(projectEventInfo);

  if (info && info !== null) {
    if (info.wasDryingSuccessful) {
      return vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingSuccessful" : "restorationSuccessful"}`).toString();
    }

    return vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingNotSuccessful" : "restorationNotSuccessful"}`).toString();
  }

  return ""; 
}

function getDryingNecessaryText(
  vue: Vue,
  projectEventInfo?: string,
  hasDrying = true
) {
  const info = getInfo<DryingNecessaryInfo>(projectEventInfo);

  if (info && info !== null) {
    let dryingNecessaryString = null;

    if (info.conclusionState && info.conclusionState !== null) {
      dryingNecessaryString = getDamagePatternConclusionStateString(vue, info.conclusionState, hasDrying);
    }
    else if (info.isDryingNecessary !== null) {
      if (info.isDryingNecessary) {
        dryingNecessaryString = vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingNecessary" : "restorationNecessary"}`).toString();
      }
      else {
        dryingNecessaryString = vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingNotNecessary" : "restorationNotNecessary"}`).toString();
      }
    }

    const userLookup = new UserLookup(UserModule.activeUsersOfClientIncludingIresAdmin);
    const technician = info.technicianId ? userLookup.getUser(info.technicianId) : null;
    const technicianName = technician ? getFullName(technician) : null;

    return joinToString(
      [
        dryingNecessaryString,
        technicianName
      ],
      ", "
    );
  }

  return null; 
}

function getMeasurementCreatedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<MeasurementCreationInfo>(projectEventInfo);

  if (info && info !== null) {
    return vue.$t("projectLogs.eventLogs.measurementCreated", [info.measurementSeriesNumber]).toString();
  }

  return "";
}

function getMeasurementSeriesCreatedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<MeasurementSeriesCreationInfo>(projectEventInfo);
  
  if (info && info !== null) {
    const descriptionString = isNullOrBlank(info.description)
        ? info.measurementSeriesNumber.toString()
        : `${info.measurementSeriesNumber} ${info.description}`;

    return !info.isUserEdited
      ? vue.$t("projectLogs.eventLogs.measurementSeriesCreatedK2WithDescriptionWithoutTile", [descriptionString]).toString()
      : vue.$t("projectLogs.eventLogs.measurementSeriesCreatedK2WithDescription", [descriptionString]).toString();
  }

  return "";
}

function getMeasurementSeriesEditedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<MeasurementSeriesCreationInfo>(projectEventInfo);
  
  if (info && info !== null) {
    const descriptionString = isNullOrBlank(info.description)
        ? info.measurementSeriesNumber.toString()
        : `${info.measurementSeriesNumber} ${info.description}`;

    return !info.isUserEdited
      ? vue.$t("projectLogs.eventLogs.measurementSeriesChangedK2WithDescriptionWithoutTile", [descriptionString]).toString()
      : vue.$t("projectLogs.eventLogs.measurementSeriesChangedK2WithDescription", [descriptionString]).toString();
  }

  return "";
}

function getProjectAssignedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<ProjectAssignedInfo>(projectEventInfo);
  
  if (info && info !== null) {
    const userLookup = new UserLookup(UserModule.activeUsersOfClientIncludingIresAdmin);
    const user = info.newProjectOwnerId ? userLookup.getUser(info.newProjectOwnerId) : null;
    const userDisplayName = user ? getDisplayName(user) : null;

    if (userDisplayName) {
      return vue.$t("projectLogs.eventLogs.projectAssignedWithUserInfo", [userDisplayName]).toString();
    }
  }

  return vue.$t("projectLogs.eventLogs.projectAssigned").toString();
}

function getProjectCreatedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<NewProjectCreatedInfo>(projectEventInfo);

  if (info && info !== null) {
    const damageDay = info.damageDay ? parseFirestoreTimestampString(info.damageDay) : null;

    if (damageDay) {
      const damageDayString = formatJsDate(damageDay);

      return vue.$t("projectLogs.eventLogs.projectCreationWithDamageDay", [damageDayString]).toString();
    }
  }

  return vue.$t("projectLogs.eventLogs.projectCreation").toString();
}

function getProjectSectionFinishedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<SectionInfo>(projectEventInfo);

  if (info && info !== null) {
    if (info.projectSection) {
      switch (info.projectSection) {
        case ProjectSection.CLIENT_DATA:
          return vue.$t("projectLogs.eventLogs.clientDataCompleted").toString();
        case ProjectSection.DOCUMENTATION_AND_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.finished").toString();
        case ProjectSection.DAMAGE_PATTERN:
          return vue.$t("projectLogs.eventLogs.damageAssessmentFinished").toString();
        case ProjectSection.DAMAGE_PATTERN_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.damagePatternConclusion").toString();
        case ProjectSection.SETUP_DRYING:
          return vue.$t("projectLogs.eventLogs.dryingSetupFinished").toString();
        case ProjectSection.SETUP_DEVICES:
          return vue.$t("projectLogs.eventLogs.devicesSetupCompleted").toString();
        case ProjectSection.DRYING_PROGRESS:
          return vue.$t("projectLogs.eventLogs.dryingProgressCompletedMeasurement").toString();
        case ProjectSection.PACKUP_DEVICES:
          return vue.$t("projectLogs.eventLogs.packupDevicesFinished").toString();
        case ProjectSection.DEVICES:
          return vue.$t("projectLogs.eventLogs.packupDevicesFinished").toString();
        case ProjectSection.DRYING_PROJECT_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.projectFinished").toString();
        case ProjectSection.PREPARE_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.leakDetectionPreparationFinished").toString();
        case ProjectSection.EXECUTE_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.leakDetectionExecutionCompleted").toString();
        case ProjectSection.LEAK_DETECTION_PROJECT_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.dryingProjectFinished").toString();

        default:
          return "";
      }
    }
  }

  return "";
}

function getProjectSectionStartedText(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<SectionInfo>(projectEventInfo);

  if (info && info !== null) {
    if (info.projectSection) {
      switch (info.projectSection) {
        case ProjectSection.CLIENT_DATA:
          return vue.$t("projectLogs.eventLogs.clientDataCompleted").toString();
        case ProjectSection.DOCUMENTATION_AND_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.leakDetectionStarted").toString();
        case ProjectSection.DAMAGE_PATTERN:
          return vue.$t("projectLogs.eventLogs.damageAssessmentStarted").toString();
        case ProjectSection.DAMAGE_PATTERN_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.damagePatternStarted").toString();
        case ProjectSection.SETUP_DRYING:
          return vue.$t("projectLogs.eventLogs.dryingSetupStarted").toString();
        case ProjectSection.SETUP_DEVICES:
          return vue.$t("projectLogs.eventLogs.mergedDevicesSetup").toString();
        case ProjectSection.DRYING_PROGRESS:
          return vue.$t("projectLogs.eventLogs.dryingProgressStarted").toString();
        case ProjectSection.PACKUP_DEVICES:
          return vue.$t("projectLogs.eventLogs.packupDevicesStarted").toString();
        case ProjectSection.DEVICES:
          return vue.$t("projectLogs.eventLogs.packupDevicesStarted").toString();
        case ProjectSection.DRYING_PROJECT_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.projectConclusionStarted").toString();
        case ProjectSection.PREPARE_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.leakDetectionPreparationStarted").toString();
        case ProjectSection.EXECUTE_LEAK_DETECTION:
          return vue.$t("projectLogs.eventLogs.leakDetectionExecutionStarted").toString();
        case ProjectSection.LEAK_DETECTION_PROJECT_CONCLUSION:
          return vue.$t("projectLogs.eventLogs.leakDetectionProjectConclusionStarted").toString();

        default:
          return "";
      }
    }
  }

  return "";
}

function getProjectCompletedText(
  vue: Vue,
  projectEventInfo?: string,
  hasDrying = true
) {
  const info = getInfo<ProjectCompletedValue>(projectEventInfo);

  if (info && info !== null) {
    let wasDryingSuccessfulText = null;

    if (info.wasDryingSuccessful === false) {
      wasDryingSuccessfulText = vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingNotSuccessful" : "restorationSuccessful"}`).toString();
    }
    else if (info.wasDryingSuccessful === true) {
      wasDryingSuccessfulText = vue.$t(`projectLogs.eventLogs.${hasDrying ? "dryingSuccessful" : "restorationSuccessful"}`).toString();
    }

    return wasDryingSuccessfulText === null
      ? joinToString(
        [
          vue.$t("projectLogs.eventLogs.dryingCompleted").toString(),
          wasDryingSuccessfulText
        ],
        ", "
      )
      : vue.$t("projectLogs.eventLogs.projectCompletedFinalTile").toString();
  }

  return vue.$t("projectLogs.eventLogs.projectCompletedFinalTile").toString();
}

function getContactPersonAddedDescription(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<ContactPersonEventInfo>(projectEventInfo);
  if (info) {
    return vue.$t("projectLogs.eventLogs.contactPersonAdded", [info.contactPersonNumber]).toString();
  }
  return "";
}

function getContactPersonDeletedDescription(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<ContactPersonEventInfo>(projectEventInfo);
  if (info) {
    return vue.$t("projectLogs.eventLogs.contactPersonDeleted", [info.contactPersonNumber]).toString();
  }
  return "";
}

function getRoomAddedDescription(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<RoomInfo>(projectEventInfo);

  if (info && info !== null) {
    let tile = vue.$t("projectLogs.eventLogs.tile1").toString();

    if (info.isFromDamageAssesment === true) {
      tile = vue.$t("projectLogs.eventLogs.tile3").toString();
    }
    else if (info.isFromDamageAssesment === false) {
      tile = vue.$t("projectLogs.eventLogs.tile5").toString();
    }

    return vue.$t("projectLogs.eventLogs.damageAssessmentRoomAdded", [tile, info.apartmentName, info.roomName]).toString();
  }

  return ""; 
}

function getRoomDeletedDescription(
  vue: Vue,
  projectEventInfo?: string
) {
  const info = getInfo<RoomInfo>(projectEventInfo);

  if (info && info !== null) {
    let tile = vue.$t("projectLogs.eventLogs.tile1").toString();

    if (info.isFromDamageAssesment === true) {
      tile = vue.$t("projectLogs.eventLogs.tile3").toString();
    }
    else if (info.isFromDamageAssesment === false) {
      tile = vue.$t("projectLogs.eventLogs.tile5").toString();
    }

    return vue.$t("projectLogs.eventLogs.damageAssessmentRoomDeleted", [tile, info.apartmentName, info.roomName]).toString();
  }

  return ""; 
}

function getRepairAppointments(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.REPAIR,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getStepDryingConclusionAppointments(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.DRYING_CONCLUSION,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getStepIntermediateMeasurementAppointment(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.INTERMEDIATE_MEASUREMENT,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getStepDryingSetUpAppointment(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.PREPARE_DRYING,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getStepSamageAssesssmentAppointmentText(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.DAMAGE_ASSESSMENT,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getStepLeakDetectionAppointmentText(
  vue: Vue,
  projectEventInfo?: string,
  hasMultipleApartments = false,
) {
  const info = getInfo<AppointmentInfo>(projectEventInfo);

  if (info && info !== null) {
    return formatAppointmentInfo(
      vue,
      info,
      AppointmentType.LEAK_DETECTION,
      hasMultipleApartments
    ) ?? "";
  }

  return "";
}

function getProjectReopenedText(
  vue: Vue,
  source: ProjectOperationSource
) {
  switch (source) {
    case ProjectOperationSource.PROJECT_MANAGER_APP:
      return vue.$t("projectLogs.eventLogs.projectReopened").toString();
    
    default:
      return vue.$t("projectLogs.eventLogs.projectReopenedFromPortal").toString();
  }
}

export function formatProjectEventLogEvent(
  vue: Vue,
  projectEventLog: ProjectEventLog,
  isPotalOnlyProject: boolean,
  hasDrying = true,
  hasMultipleApartments = false,
) {
  const projectEventType = projectEventLog.event;
  const projectEventInfo = projectEventLog.info;
  const projectEventSource = projectEventLog.source 
    ? projectEventLog.source as ProjectOperationSource
    : isPotalOnlyProject
        ? ProjectOperationSource.PORTAL 
        : ProjectOperationSource.PROJECT_MANAGER_APP;

  switch (projectEventType) {
    case ProjectEventType.AUTO_TIME_TRACKING:
      return vue.$t("projectLogs.eventLogs.autoTimeTracking").toString();
    case ProjectEventType.CLIENT_DATA_COMPLETED:
      return vue.$t("projectLogs.eventLogs.clientDataCompleted").toString();
    case ProjectEventType.DAMAGE_ASSESSMENT_COMPLETED:
      return getDamageAssesmentCompletedText(vue, projectEventInfo);
    case ProjectEventType.DAMAGE_ASSESSMENT_PLANNED:
      return getDamageAssessmentPlannedText(vue, projectEventInfo);
    case ProjectEventType.DAMAGE_ASSESSMENT_STARTED:
      return vue.$t("projectLogs.eventLogs.damageAssessmentStarted").toString();
    case ProjectEventType.DAMAGE_PATTERN_COMPLETED:
      return getDamagePatternCompletedText(vue, projectEventInfo);
    case ProjectEventType.DAMAGE_PATTERN_REOPENED:
      return vue.$t("projectLogs.eventLogs.damagePatternReopened").toString();
    case ProjectEventType.DAMAGE_REPORT_SENT:
      return vue.$t("projectLogs.eventLogs.damageReportSent").toString();
    case ProjectEventType.DEVICES_SETUP_COMPLETED:
      return vue.$t("projectLogs.eventLogs.devicesSetupComplete").toString();
    case ProjectEventType.DOCUMENTATION_AND_LEAK_DETECTION_FINISHED:
      return getDocumentationAndLeakDetectionFinishedText(vue, projectEventInfo);
    case ProjectEventType.DRYING_FINISHED:
      return vue.$t("projectLogs.eventLogs.dryingFinished").toString();
    case ProjectEventType.DRYING_NECESSARY:
      return getDryingNecessaryText(vue, projectEventInfo, hasDrying);
    case ProjectEventType.DRYING_PROGRESS_COMPLETED_FOLLOW_UP:
      return vue.$t("projectLogs.eventLogs.dryingProgressCompletedFollowUp").toString();
    case ProjectEventType.DRYING_PROGRESS_COMPLETED_MEASUREMENT:
      return vue.$t("projectLogs.eventLogs.dryingProgressCompletedMeasurement").toString();
    case ProjectEventType.DRYING_RESULT:
      return getDryingResultText(vue, projectEventInfo, hasDrying);
    case ProjectEventType.DRYING_SETUP_COMPLETED:
      return vue.$t("projectLogs.eventLogs.dryingSetupFinished").toString();
    case ProjectEventType.DRYING_SET_UP_STARTED:
      return vue.$t("projectLogs.eventLogs.dryingSetupStarted").toString();
    case ProjectEventType.INTERIM_REPORT_SENT:
      return vue.$t("projectLogs.eventLogs.interimReportSent").toString();
    case ProjectEventType.LEAK_DETECTION_EXECUTION_COMPLETED:
      return vue.$t("projectLogs.eventLogs.leakDetectionExecutionCompleted").toString();
    case ProjectEventType.LEAK_DETECTION_PREPARATION_COMPLETED:
      return vue.$t("projectLogs.eventLogs.leakDetectionPreparationCompleted").toString();
    case ProjectEventType.LEAK_DETECTION_PROJECT_FINISHED:
      return vue.$t("projectLogs.eventLogs.leakDetectionProjectFinished").toString();
    case ProjectEventType.LEAK_DETECTION_PROJECT_REOPENED:
      return vue.$t("projectLogs.eventLogs.leakDetectionProjectReopened").toString();
    case ProjectEventType.LEAK_DETECTION_REPORT_SENT:
      return vue.$t("projectLogs.eventLogs.leakDetectionReportSent").toString();
    case ProjectEventType.LEAK_DETECTION_STARTED:
      return vue.$t("projectLogs.eventLogs.leakDetectionStarted").toString();
    case ProjectEventType.MEASUREMENT_CREATED:
      return getMeasurementCreatedText(vue, projectEventInfo);
    case ProjectEventType.MEASUREMENT_SERIES_CREATED:
      return getMeasurementSeriesCreatedText(vue, projectEventInfo);
    case ProjectEventType.MEASUREMENT_SERIES_EDITED:
      return getMeasurementSeriesEditedText(vue, projectEventInfo);
    case ProjectEventType.NOTE:
      return vue.$t("projectLogs.eventLogs.note").toString();
    case ProjectEventType.PROJECT_ASSIGNED:
      return getProjectAssignedText(vue, projectEventInfo);
    case ProjectEventType.PROJECT_COMPLETED:
      return getProjectCompletedText(vue, projectEventInfo, hasDrying);
    case ProjectEventType.PROJECT_COMPLETED_FINAL_TILE:
      return vue.$t("projectLogs.eventLogs.projectCompletedFinalTile").toString();
    case ProjectEventType.PROJECT_CONCLUSION_COMPLETED:
      return vue.$t("projectLogs.eventLogs.projectConclusionCompleted").toString();
    case ProjectEventType.PROJECT_CREATED:
      return isPotalOnlyProject
        ? vue.$t("projectLogs.eventLogs.portalProjectCreation").toString()
        : getProjectCreatedText(vue, projectEventInfo);
    case ProjectEventType.PROJECT_DELETED:
      return vue.$t("projectLogs.eventLogs.projectDeleted").toString();
    case ProjectEventType.PROJECT_DELETED_IN_APP:
      return vue.$t("projectLogs.eventLogs.projectDeletedInApp").toString();
    case ProjectEventType.PROJECT_MOVED_TO_ARCHIVED:
      return vue.$t("projectLogs.eventLogs.projectMovedToArchived").toString();
    case ProjectEventType.PROJECT_MOVED_TO_FINISHED:
      return vue.$t("projectLogs.eventLogs.projectMovedToFinished").toString();
    case ProjectEventType.PROJECT_REACTIVATED:
      return vue.$t("projectLogs.eventLogs.projectReactivated").toString();
    case ProjectEventType.PROJECT_REOPENED:
      return getProjectReopenedText(vue, projectEventSource);
    case ProjectEventType.PROJECT_REPORT_SENT:
      return vue.$t("projectLogs.eventLogs.projectReportSent").toString();
    case ProjectEventType.PROJECT_RESTORED:
      return vue.$t("projectLogs.eventLogs.projectRestored").toString();
    case ProjectEventType.PROJECT_RESULT:
      return vue.$t("projectLogs.eventLogs.projectResult").toString();
    case ProjectEventType.PROJECT_SECTION_FINISHED:
      return getProjectSectionFinishedText(vue, projectEventInfo);
    case ProjectEventType.PROJECT_SECTION_STARTED:
      return getProjectSectionStartedText(vue, projectEventInfo);
    case ProjectEventType.PROJECT_WORK_STARTED:
      return vue.$t("projectLogs.eventLogs.projectWorkStarted").toString();
    case ProjectEventType.CONTACT_PERSON_ADDED:
      return getContactPersonAddedDescription(vue, projectEventInfo);
    case ProjectEventType.CONTACT_PERSON_DELETED:
      return getContactPersonDeletedDescription(vue, projectEventInfo);
    case ProjectEventType.ROOM_ADDED:
      return getRoomAddedDescription(vue, projectEventInfo);
    case ProjectEventType.ROOM_DELETED:
      return getRoomDeletedDescription(vue, projectEventInfo);
    case ProjectEventType.STARTED_CACHED_PROJECT_EDIT:
      return vue.$t("projectLogs.eventLogs.startedCachedProjectEdit").toString();
    case ProjectEventType.STEP_DAMAGE_ASSESSMENT_APPOINTMENT:
      return getStepSamageAssesssmentAppointmentText(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_DAMAGE_ASSESSMENT_RESULT:
      return getDamageAssesmentResultText(vue, projectEventInfo, hasDrying);
    case ProjectEventType.STEP_DRYING_CONCLUSION_APPOINTMENTS:
      return getStepDryingConclusionAppointments(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_DRYING_CONCLUSION_RESULTS:
      return vue.$t("projectLogs.eventLogs.stepDryingConclusionResults").toString();
    case ProjectEventType.STEP_DRYING_SET_UP_APPOINTMENTS:
      return getStepDryingSetUpAppointment(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_DRYING_SET_UP_COMPLETIONS:
      return vue.$t("projectLogs.eventLogs.stepDryingSetUpCompletions").toString();
    case ProjectEventType.STEP_FIRST_CONTACT:
      return vue.$t("projectLogs.eventLogs.stepFirstContact").toString();
    case ProjectEventType.STEP_INTERMEDIATE_MEASUREMENT_APPOINTMENTS:
      return getStepIntermediateMeasurementAppointment(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_INTERMEDIATE_MEASUREMENT_COMPLETIONS:
      return vue.$t("projectLogs.eventLogs.stepIntermediateMeasurementCompletions").toString();
    case ProjectEventType.STEP_LEAK_DETECTION_APPOINTMENT:
      return getStepLeakDetectionAppointmentText(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_LEAK_DETECTION_RESULT:
      return vue.$t("projectLogs.eventLogs.stepLeakDetectionResult").toString();
    case ProjectEventType.STEP_OFFER_RESULT_DRYING_APPROVED:
      return vue.$t(`projectLogs.eventLogs.${hasDrying ? "stepOfferResultDryingApproved" : "stepOfferResultRestorationApproved"}`).toString();
    case ProjectEventType.STEP_OFFER_RESULT_DRYING_NOT_APPROVED:
      return vue.$t("projectLogs.eventLogs.stepOfferResultDryingNotApproved").toString();
    case ProjectEventType.STEP_OFFER_SEND_TO_INSURANCE:
      return vue.$t("projectLogs.eventLogs.stepOfferSendToInsurance").toString();
    case ProjectEventType.STEP_PROJECT_ACCEPTED:
      return vue.$t("projectLogs.eventLogs.stepProjectAccepted").toString();
    case ProjectEventType.STEP_PROJECT_APPROVED_PROJECT_FINISHED:
      return vue.$t("projectLogs.eventLogs.stepProjectApprovedProjectFinished").toString();
    case ProjectEventType.STEP_PROJECT_APPROVED_PROJECT_SAVED:
      return vue.$t("projectLogs.eventLogs.stepProjectApprovedProjectSaved").toString();
    case ProjectEventType.STEP_PROJECT_RESULT:
      return vue.$t("projectLogs.eventLogs.stepProjectResult").toString();
    case ProjectEventType.STEP_REPAIR_APPOINTMENTS:
      return getRepairAppointments(vue, projectEventInfo, hasMultipleApartments);
    case ProjectEventType.STEP_REPAIR_START:
      return vue.$t("projectLogs.eventLogs.stepRepairStart").toString();
    case ProjectEventType.TIME_TRACKING:
      return vue.$t("projectLogs.eventLogs.timeTracking").toString();
    case ProjectEventType.TIME_TRACKING_REPORT_SENT:
      return vue.$t("projectLogs.eventLogs.timeTrackingReportSent").toString();
    case ProjectEventType.REPAIR_STARTED:
      return vue.$t("projectLogs.eventLogs.repairStarted").toString();
    case ProjectEventType.REPAIR_COMPLETED:
      return vue.$t("projectLogs.eventLogs.repairCompleted").toString();

    default:
      return ""
  }
}

/**
 * The apartment label, see {@link formatApartmentDetails}: "Wohnung 2: H. Hubschneider (Keller)",
 * or "Wohnung H. Hubschneider (Keller)" with the number hidden (a project with a single flat).
 * A Strombezug is labelled by its full name alone.
 *
 * @param hideFloor drops the Adresszusatz next to a name
 * @param hideContactPersonNumber hides the number, for a project with a single flat
 */
export function formatContactPersonName(
  vue: Vue,
  contactPersonInfo: ContactPersonInfo,
  hideFloor?: boolean,
  hideContactPersonNumber?: boolean,
) {
  if (isOnlyUsedForElectricityBilling(contactPersonInfo)) {
    const lastName = nullIfBlank(contactPersonInfo.lastName);
    const firstName = nullIfBlank(contactPersonInfo.firstName);

    return nullIfBlank(joinToString([firstName, lastName], " ")) ?? "";
  }

  const showsNumber = !hideContactPersonNumber;
  const apartment = joinToString(
    [
      vue.$t("projectLogs.noteLogs.apartment").toString(),
      showsNumber ? contactPersonInfo.contactPersonNumber.toString() : null
    ],
    " "
  );

  return joinApartmentLabel(apartment, formatApartmentDetails(contactPersonInfo, !hideFloor), showsNumber);
}

export function formatApartmentName(
  vue: Vue,
  noteApartmentInfo: NoteApartmentInfo,
  contactPersons: ContactPersonInfo[],
  hideContactPersonNumber?: boolean,
) {
  // Stripped on both sides so a note that stored an electricity-billing apartment without the
  // ELECTRICITY_BILLING_ prefix still resolves against the now-prefixed synthetic contact person.
  const targetContactPersonId = stripElectricityBillingPrefix(noteApartmentInfo.contactPersonId);
  const contactPersonInfo = contactPersons.find(
    (cp) => stripElectricityBillingPrefix(cp.id) === targetContactPersonId
  );

  if (contactPersonInfo) {
    return formatContactPersonName(
      vue,
      contactPersonInfo,
      false,
      hideContactPersonNumber
    );
  }

  return noteApartmentInfo.apartmentName;
}

/**
 * "Raum 3: Küche", "Raum 3: Bad" or "Raum 3", see {@link getRoomDescription} for which entry names
 * the room and what `usageOptions` is.
 */
export function formatRoomName(vue: Vue, roomNumber: number, room?: Room, usageOptions: string[] | null = null) {
  const numberedRoom = vue.$t("projectLogs.noteLogs.roomName", [roomNumber]).toString();

  return joinRoomLabel(numberedRoom, getRoomDescription(room, usageOptions));
}

/**
 * The note's room as the project has it now. A note stores its room's label when it is written, and
 * IRES Project renumbers rooms 1..n when one is deleted, so a stored "Raum 2" can name another room
 * by now. A room that no longer exists keeps the label it was saved with.
 */
export function resolveNoteRoomInfo(
  vue: Vue,
  noteRoom: NoteRoomInfo,
  rooms: Room[],
  usageOptions: string[] | null = null
): NoteRoomInfo {
  const room = rooms.find((candidate) => candidate.id === noteRoom.roomId);

  if (!room) {
    return noteRoom;
  }

  return {
    ...noteRoom,
    roomNumber: room.roomNumber,
    roomName: formatRoomName(vue, room.roomNumber, room, usageOptions),
  };
}

export function formatProjectNoteLogEvent(
  vue: Vue,
  projectNoteLog: ProjectNoteLog,
  contactPersons: ContactPersonInfo[],
  rooms: Room[],
  usageOptions: string[] | null,
  hideContactPersonNumber?: boolean,
) {
  const apartmentAndRoomInfo = nullIfBlank(
    joinToString(
      [
        projectNoteLog.targetApartment
          ? formatApartmentName(
              vue,
              projectNoteLog.targetApartment,
              contactPersons,
              hideContactPersonNumber
            )
          : null,
        projectNoteLog.targetRoom
          ? resolveNoteRoomInfo(vue, projectNoteLog.targetRoom, rooms, usageOptions).roomName
          : null
      ],
      ", "
    )
  );

  const noteTitle = apartmentAndRoomInfo === null
    ? vue.$t("projectLogs.noteLogs.note").toString()
    : vue.$t("projectLogs.noteLogs.noteIn", [apartmentAndRoomInfo]).toString();

  return `${noteTitle}\n${projectNoteLog.note ?? ""}`;
}

export function createProjectStatusLogs(
  vue: Vue,
  projectSteps: ProjectStepsContainer,
  projectOverview: DryingProjectOverview,
  projectDetails: ProjectDetails
) {
  return getStepDescriptionForLogEntries(
    vue,
    projectSteps,
    projectOverview,
    projectDetails
  ).map((stepDescription) => {
    const projectStatusLog: ProjectStatusLog = {
      id: uuidv4(),
      createdBy: {
        recordedAt: dateToTimestampParser(stepDescription.stepCreatedBy.recordedAt)
          ?? stepDescription.stepCreatedBy.recordedAt,
        recordedByUserId: stepDescription.stepCreatedBy.recordedByUserId
      },
      content: stepDescription.stepDescription,
      logType: ProjectLogType.STATUS,
      source: stepDescription.stepSource
        ? stepDescription.stepSource as ProjectOperationSource
        : isPortalOnlyProject(projectDetails)
            ? ProjectOperationSource.PORTAL 
            : ProjectOperationSource.PROJECT_MANAGER_APP,
    };

    return projectStatusLog;
  });
}